import React, { useEffect, useState, useMemo } from 'react';
import { subDays, isWithinInterval, isSameDay, parse } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import classNames from 'classnames';
import useSelectedDays from './hooks/useSelectedDays';

import styles from './DatePickerCalendar.module.scss';

export const HISTORIC_DATE_FORMAT = 'yyyy-MM-dd';
export const HISTORIC_DISPLAY_DATE_FORMAT = 'LLLL dd, yyyy';

export const DatePickerDay = (
  props: PickersDayProps<Date> & {
    selectedDay?: Date | null;
    range: number;
    enableFuture: boolean;
  },
) => {
  const { day, selectedDay, range, enableFuture } = props;
  const { start, end } = useSelectedDays({ selectedDay, range, enableFuture });
  // Ensure we're working with Date objects for date-fns functions
  const dayAsDate = day instanceof Date ? day : new Date(day as any);
  const dayIsBetween = isWithinInterval(dayAsDate, { start, end });
  const isFirstDay = isSameDay(dayAsDate, start);
  const isLastDay = isSameDay(dayAsDate, end);

  return (
    <div className={styles.dayOuter}>
      <div
        className={classNames(
          isLastDay && styles.lastDayWrapper,
          isFirstDay && styles.firstDayWrapper,
          dayIsBetween && styles.selectedDayWrapper,
        )}
      >
        <PickersDay
          {...props}
          className={classNames(
            isLastDay && styles.lastDay,
            isFirstDay && styles.firstDay,
            dayIsBetween && styles.selectedDay,
          )}
          data-testid="date-picker-calendar-day"
          disableMargin
          disableRipple
          disableTouchRipple
          day={day}
        />
      </div>
    </div>
  );
};

export const DatePickerCalendar = ({
  getStartDate,
  startDate,
  minDate,
  maxDate,
  range,
  enableFuture = false,
}: {
  getStartDate: (date: Date | null) => void;
  startDate: string | undefined;
  minDate: Date;
  maxDate: Date;
  range: number;
  enableFuture?: boolean;
}) => {
  const fallbackStartDate = enableFuture ? new Date() : subDays(new Date(), 1);
  const defaultStartDate = startDate
    ? parse(startDate, HISTORIC_DATE_FORMAT, new Date())
    : fallbackStartDate;

  const [value, setValue] = useState<Date | null>(defaultStartDate);
  const slots = useMemo(() => ({ day: DatePickerDay }), []);
  const slotProps = useMemo(
    () => ({
      day: {
        selectedDay: value,
        range,
        enableFuture,
      },
    }),
    [range, value, enableFuture],
  );

  const handleChange = (newValue: any) => {
    // Handle the new onChange signature from MUI X v8
    // Convert the value to Date if it's not already
    if (newValue === null) {
      setValue(null);
    } else if (newValue instanceof Date) {
      setValue(newValue);
    } else {
      // Handle other date types (like Dayjs) by converting to Date
      setValue(new Date(newValue.toString()));
    }
  };

  useEffect(() => {
    getStartDate(value);
    // Do not want to update every time the getStartDate prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enableFuture]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateCalendar
        className={styles.datePickerCalendar}
        data-testid="date-picker-calendar"
        disableFuture={!enableFuture}
        maxDate={maxDate}
        minDate={minDate}
        onChange={handleChange}
        slots={slots as any}
        slotProps={slotProps as any}
        value={value}
      />
    </LocalizationProvider>
  );
};

export default DatePickerCalendar;
